import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  ArrowRight, 
  ShieldCheck, 
  Award, 
  Users, 
  Clock, 
  Phone, 
  Mail, 
  CheckCircle, 
  Palette, 
  Hammer, 
  Building2, 
  Home as HomeIcon, 
  Layers, 
  Sun, 
  Check
} from 'lucide-react';
import SEO from '../components/SEO';
import ReviewSlider from '../components/ReviewSlider';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/seo';

// Default static service data ensuring instantaneous rendering and full fallback
const DEFAULT_SERVICES = [
  {
    ref_slug: 'ydelser-maling-af-vaegge',
    slug: 'maling-af-vaegge',
    href: '/ydelser/maling-af-vaegge',
    title: 'Maling af vægge',
    category: 'Indendørs & Bolig',
    categoryId: 'bolig',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/fb1ba773-6f6e-4a2c-ad28-a29b362c6eb1.jpg',
    desc: 'Professionel maling af vægge og lofter med fokus på knivskarp finish, harmoniske farvevalg og kvalitetsmaling med høj dækkeevne.',
    features: [
      'Grundigt forarbejde og omhyggelig afdækning',
      'Harmoniske og holdbare farvevalg',
      'Enestående dækkeevne og slidstærk overflade',
      'Udføres af uddannede malere uden akkordpres',
    ],
    icon: Palette,
  },
  {
    ref_slug: 'ydelser-spartling-af-vaegge',
    slug: 'spartling-af-vaegge',
    href: '/ydelser/spartling-af-vaegge',
    title: 'Spartling af vægge',
    category: 'Indendørs & Bolig',
    categoryId: 'bolig',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/b717f329-9b38-47ab-b4ad-ddf4fd8d982c.jpg',
    desc: 'Fuldspartling, samlespartling og slibning af vægge, der skaber det perfekte, silkebløde og plane grundlag for filt, tapet eller maling.',
    features: [
      'Professionel fuldspartling og planslibning',
      'Specialeffekter som betonlook og KABE-spartel',
      'Fjerner ujævnheder, revner og gamle skader',
      'Moderne sprøjtespartelanlæg til store flader',
    ],
    icon: Hammer,
  },
  {
    ref_slug: 'ydelser-opsaetning-af-filt',
    slug: 'opsaetning-af-filt',
    href: '/ydelser/opsaetning-af-filt',
    title: 'Opsætning af filt',
    category: 'Indendørs & Bolig',
    categoryId: 'bolig',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/95374d40-7b43-4332-b90d-847f8df2ef5f.jpg',
    desc: 'Opsætning af glasfilt og glasvæv forstærker vægoverfladen, modvirker revnedannelser og skaber en ensartet, elegant og rolig struktur.',
    features: [
      'Glat og ensartet finish uden synlige samlinger',
      'Forstærker gipsvægge og forebygger revner',
      'Vælg mellem fin glasfilt eller slidstærkt glasvæv',
      'Forlænger væggenes levetid og modstandsdygtighed',
    ],
    icon: Layers,
  },
  {
    ref_slug: 'ydelser-tapet',
    slug: 'tapet',
    href: '/ydelser/tapet',
    title: 'Opsætning af tapet',
    category: 'Indendørs & Bolig',
    categoryId: 'bolig',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/156a77a4-90b0-49bb-b5ea-e65043a3783d.jpg',
    desc: 'Giv rummet karakter, varme og personlighed med professionel tapetsering af mønstertapeter, fotostater, naturmaterialer og savsmuldstapet.',
    features: [
      'Præcis mønsterpasning og usynlige samlinger',
      'Opsætning af alle tapettyper, velour, kork og tekstiler',
      'Rådgivning om rummets proportioner og belysning',
      'Perfekt klargøring af underlaget inden opsætning',
    ],
    icon: Sparkles,
  },
  {
    ref_slug: 'ydelser-udvendigt-malerarbejde',
    slug: 'udvendigt-malerarbejde',
    href: '/ydelser/udvendigt-malerarbejde',
    title: 'Udvendigt malerarbejde',
    category: 'Special & Udvendigt',
    categoryId: 'special',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/96ca710d-a5d8-405c-82da-48301c3b7bcc.jpg',
    desc: 'Robust vejrbeskyttelse og visuel fornyelse af facader, træværk, gavle, døre, vinduer og tagudhæng tilpasset det omskiftelige danske klima.',
    features: [
      'Maling af facader, sokler, gavle og udhæng',
      'Træbeskyttelse og maling af døre og vinduer',
      'Høj modstandsdygtighed over for fugt og sollys',
      'Forlænger bygningens levetid markant',
    ],
    icon: Sun,
  },
  {
    ref_slug: 'ydelser-erhvervs-og-nybyggeri',
    slug: 'erhvervs-og-nybyggeri',
    href: '/ydelser/erhvervs-og-nybyggeri',
    title: 'Erhvervs- og nybyggeri',
    category: 'Erhverv & Nybyg',
    categoryId: 'erhverv',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/d8eccbb4-67a8-4abe-b735-6e29e1d2053c.jpg',
    desc: 'Erfarne malerentrepriser til erhvervsbyggeri, kontordomiciler og nybyggeri med kapacitet, maskinpark og disciplin til at overholde stramme tidsplaner.',
    features: [
      'Kapacitet til store del- og totalentrepriser',
      'Moderne udstyr: airless sprøjteanlæg og sakslifte',
      'Erfaring fra projekter som Lighthouse og DNU Skejby',
      'Fast kontaktperson og tæt koordinering med byggeledelse',
    ],
    icon: Building2,
  },
  {
    ref_slug: 'ydelser-erhverv',
    slug: 'erhverv',
    href: '/ydelser/erhverv',
    title: 'Erhvervsmaler',
    category: 'Erhverv & Nybyg',
    categoryId: 'erhverv',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/00d1f8e8-ea1e-4beb-b2b3-8afe34ff51d1.jpg',
    desc: 'Pålidelig erhvervspartner til virksomheder, detailbutikker, kontorer og boligselskaber med faste serviceaftaler og fleksible arbejdstider.',
    features: [
      'Løbende vedligeholdelse og opfriskning',
      'Minimal forstyrrelse af virksomhedens daglige drift',
      'Skræddersyede service- og rammeaftaler',
      'Hurtig udrykning ved akutte behov eller skader',
    ],
    icon: Building2,
  },
  {
    ref_slug: 'ydelser-private',
    slug: 'private',
    href: '/ydelser/private',
    title: 'Malerarbejde for private',
    category: 'Indendørs & Bolig',
    categoryId: 'bolig',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/65edb7cb-b2dd-4556-9798-241f24eff286.jpg',
    desc: 'Kvalitetsmalerarbejde til private boligejere i villaer, rækkehuse og lejligheder, hvor der værnes om det gode håndværk og den trygge dialog.',
    features: [
      'Stor respekt for dit private hjem og dine ejendele',
      'Grundig afdækning og fejlfri oprydning hver dag',
      'Uddannede svende og lærlinge med sundt arbejdsmiljø',
      'Omfattet af Danske Malermestres Garantiordning',
    ],
    icon: HomeIcon,
  },
  {
    ref_slug: 'ydelser-vedligeholdelsesarbejde',
    slug: 'vedligeholdelsesarbejde',
    href: '/ydelser/vedligeholdelsesarbejde',
    title: 'Vedligeholdelsesarbejde',
    category: 'Erhverv & Nybyg',
    categoryId: 'erhverv',
    image: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/95184475-f738-42ad-8d8f-fbf41b0cdb58.jpg',
    desc: 'Forebyggende vedligeholdelse af ejendomme, skoler, boligforeninger og private boliger forlænger bygningsdelenes levetid og sparer omkostninger.',
    features: [
      'Regelmæssigt eftersyn af vinduer, døre og facader',
      'Professionel fugning og udbedring af sætningsskader',
      'Mindre reparationer udføres hurtigt og effektivt',
      'Faste vedligeholdelsesplaner med overskuelige budgetter',
    ],
    icon: Clock,
  },
];

const CATEGORIES = [
  { id: 'alle', label: 'Alle ydelser', count: 9 },
  { id: 'bolig', label: 'Indendørs & Bolig', count: 5 },
  { id: 'erhverv', label: 'Erhverv & Nybyg', count: 3 },
  { id: 'special', label: 'Special & Udvendigt', count: 1 },
];

const PROCESS_STEPS = [
  {
    step: '01',
    title: 'Uforpligtende dialog & rådgivning',
    desc: 'Vi starter med en grundig samtale om dine ønsker og behov. Vi rådgiver om farver, materialer og holdbarhed, så du får den rette løsning.',
  },
  {
    step: '02',
    title: 'Gennemskueligt tilbud & fast pris',
    desc: 'Du modtager et præcist og gennemskueligt tilbud med en fast pris og en klar tidsplan – ingen ubehagelige overraskelser eller skjulte gebyrer.',
  },
  {
    step: '03',
    title: 'Omhyggelig afdækning & udførelse',
    desc: 'Vores faglærte malersvende dækker omhyggeligt af og udfører arbejdet efter stolte håndværkstraditioner med fokus på den sidste finish.',
  },
  {
    step: '04',
    title: 'Grundig gennemgang & ren oprydning',
    desc: 'Vi rydder pænt op efter os og gennemgår det færdige resultat sammen med dig for at sikre, at du er 100 % tilfreds med arbejdet.',
  },
];

export default function Ydelser() {
  const [activeCategory, setActiveCategory] = useState('alle');
  const [services, setServices] = useState(DEFAULT_SERVICES);

  // Sync with CMS content dynamically
  useEffect(() => {
    let isMounted = true;
    const fetchServicesFromCMS = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/pages');
        if (!res.ok) return;
        const pages = await res.json();
        if (!isMounted || !Array.isArray(pages)) return;

        // Enrich default services if CMS has updated data
        const cmsServices = pages.filter(p => p.parent_slug === 'ydelser');
        if (cmsServices.length > 0) {
          const merged = DEFAULT_SERVICES.map(service => {
            const cmsMatch = cmsServices.find(p => p.ref_slug === service.ref_slug);
            if (!cmsMatch) return service;
            return {
              ...service,
              title: cmsMatch.title || service.title,
              image: (cmsMatch.images && cmsMatch.images[0]) || service.image,
              desc: cmsMatch.meta_description || service.desc,
            };
          });
          setServices(merged);
        }
      } catch {
        // Fallback to DEFAULT_SERVICES
      }
    };

    fetchServicesFromCMS();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredServices = activeCategory === 'alle'
    ? services
    : services.filter(s => s.categoryId === activeCategory);

  return (
    <div className="bg-white text-slate-800">
      {/* Dynamic SEO Tag */}
      <SEO
        title="Ydelser & Malerarbejde | Professionel Malermester | BM Maler A/S"
        description="Se alle vores ydelser inden for malerarbejde: maling af vægge, spartling, opsætning af filt, tapet, erhvervsbyggeri, epoxymaling og facademaling i Vejle og omegn."
        canonical="/ydelser"
        image="https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/fb1ba773-6f6e-4a2c-ad28-a29b362c6eb1.jpg"
        extraSchemas={[
          {
            '@type': 'HowTo',
            name: 'Sådan forløber dit malerprojekt hos BM Maler',
            description: 'Trin-for-trin proces for et professionelt malerprojekt fra uforpligtende dialog og fast tilbud til omhyggelig udførelse og aflevering.',
            step: PROCESS_STEPS.map((step, idx) => ({
              '@type': 'HowToStep',
              position: idx + 1,
              name: step.title,
              text: step.desc,
            })),
          },
        ]}
      />

      {/* Hero Section */}
      <section className="relative min-h-[48vh] flex items-center justify-center overflow-hidden bg-slate-950 text-white py-24 sm:py-28">
        {/* Background Image with Ambient Overlays */}
        <div className="absolute inset-0 z-0">
          <motion.img
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.45 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/fb1ba773-6f6e-4a2c-ad28-a29b362c6eb1.jpg', { width: 1600, quality: 85 })}
            alt="BM Maler professionelt malerarbejde og ydelser"
            width="1600"
            height="600"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="flex justify-center items-center gap-2 text-xs text-slate-400">
            <Link to="/" className="hover:text-[#F1B528] transition-colors">Forside</Link>
            <span className="text-slate-600">/</span>
            <span className="text-[#F1B528] font-medium">Ydelser</span>
          </nav>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full text-[#F1B528] text-xs font-bold uppercase tracking-wider shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Knap 70 års håndværkserfaring</span>
          </motion.div>

          {/* Page Heading */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight"
          >
            Vores <span className="text-[#F1B528]">ydelser</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-300 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Hos BM Maler leverer vi komplette malerløsninger til private boligejere, erhverv, boligforeninger og nybyggeri. Med fokus på kompromisløs kvalitet, ordentlighed og den perfekte finish.
          </motion.p>

          {/* Action CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center items-center gap-4 pt-4"
          >
            <a
              href="#ydelser-oversigt"
              className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 font-bold px-7 py-3.5 rounded-full text-sm transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 cursor-pointer"
            >
              Se alle ydelser
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="tel:+4575695900"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-full text-sm border border-white/20 transition-all duration-300 backdrop-blur-sm"
            >
              <Phone className="w-4 h-4 text-[#F1B528]" />
              75 69 59 00
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust & Guarantee Strip */}
      <section className="bg-slate-900 text-white border-y border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
            <div className="flex items-center gap-3.5 justify-center md:justify-start">
              <div className="p-2.5 rounded-xl bg-[#F1B528]/10 text-[#F1B528] border border-[#F1B528]/20 shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <span className="block text-sm font-bold text-white">Garantiordning</span>
                <span className="text-xs text-slate-400">Danske Malermestre</span>
              </div>
            </div>

            <div className="flex items-center gap-3.5 justify-center md:justify-start">
              <div className="p-2.5 rounded-xl bg-[#F1B528]/10 text-[#F1B528] border border-[#F1B528]/20 shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <span className="block text-sm font-bold text-white">Siden 1957</span>
                <span className="text-xs text-slate-400">Knap 70 års erfaring</span>
              </div>
            </div>

            <div className="flex items-center gap-3.5 justify-center md:justify-start">
              <div className="p-2.5 rounded-xl bg-[#F1B528]/10 text-[#F1B528] border border-[#F1B528]/20 shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <span className="block text-sm font-bold text-white">Faglærte malere</span>
                <span className="text-xs text-slate-400">Uddannede svende</span>
              </div>
            </div>

            <div className="flex items-center gap-3.5 justify-center md:justify-start">
              <div className="p-2.5 rounded-xl bg-[#F1B528]/10 text-[#F1B528] border border-[#F1B528]/20 shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <span className="block text-sm font-bold text-white">Fast pris & aftale</span>
                <span className="text-xs text-slate-400">Ingen skjulte gebyrer</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Overview Section */}
      <section id="ydelser-oversigt" className="py-20 sm:py-28 bg-slate-50/50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
            <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full inline-block">
              Vores ekspertiseområder
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              Oversigt over alle ydelser
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              Vælg en ydelse for at læse mere om vores fremgangsmåde, materialer og muligheder, eller kontakt os for et skræddersyet tilbud.
            </p>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center items-center gap-2.5 mb-14">
            {CATEGORIES.map((cat) => {
              const isSelected = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer ${
                    isSelected
                      ? 'bg-slate-950 text-[#F1B528] shadow-md scale-105'
                      : 'bg-white text-slate-600 hover:text-slate-950 hover:bg-slate-100 border border-slate-200 shadow-sm'
                  }`}
                >
                  {cat.label}
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                    isSelected ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Services Grid */}
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence>
              {filteredServices.map((service) => {
                const Icon = service.icon || Palette;
                return (
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    key={service.slug}
                    className="flex flex-col bg-white rounded-3xl overflow-hidden border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-[#F1B528]/50 transition-all duration-300 group"
                  >
                    {/* Card Image */}
                    <div className="relative h-60 overflow-hidden bg-slate-100 shrink-0">
                      <img
                        src={getOptimizedImageUrl(service.image, { width: 700, quality: 80 })}
                        alt={`${service.title} - BM Maler A/S`}
                        width="700"
                        height="400"
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-slate-900/85 backdrop-blur-md text-[#F1B528] text-xs font-bold px-3 py-1.5 rounded-full border border-white/10 shadow-sm flex items-center gap-1.5">
                          <Icon className="w-3.5 h-3.5 text-[#F1B528]" />
                          {service.category}
                        </span>
                      </div>

                      {/* Card Title on Image */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight drop-shadow-md">
                          {service.title}
                        </h3>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-6 sm:p-7 flex flex-col flex-grow justify-between space-y-6">
                      <div className="space-y-4">
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {service.desc}
                        </p>

                        {/* Feature bullets */}
                        <div className="border-t border-slate-100 pt-4 space-y-2.5">
                          <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                            Fordele & Detaljer
                          </span>
                          <ul className="space-y-2">
                            {service.features.map((feat, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                <span>{feat}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Card Actions */}
                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                        <Link
                          to={service.href}
                          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-[#F1B528] text-white hover:text-slate-950 px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 group-hover:bg-[#F1B528] group-hover:text-slate-950"
                        >
                          Læs mere om {service.title.toLowerCase()}
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <a
                          href="tel:+4575695900"
                          title="Ring og få et tilbud"
                          className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-950 transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Process / Working Flow Section */}
      <section className="py-20 sm:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full inline-block">
              Tryg proces
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Sådan forløber dit malerprojekt
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">
              Når du vælger BM Maler, er du i trygge hænder fra den indledende snak til den endelige aflevering.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS_STEPS.map((step, idx) => (
              <div 
                key={idx}
                className="relative bg-slate-50 p-8 rounded-3xl border border-slate-100 space-y-4 flex flex-col justify-between"
              >
                <div>
                  <span className="block text-4xl font-black text-[#F1B528] mb-4">
                    {step.step}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
                <div className="w-8 h-1 bg-[#F1B528] rounded-full mt-4" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review Slider for Social Proof */}
      <section className="py-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F1B528] rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <ReviewSlider />
        </div>
      </section>

      {/* Direct Contact & Quote Form Section */}
      <section className="py-20 sm:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Direct contact info */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="text-[#D99C1B] text-xs font-bold uppercase tracking-wider bg-[#F1B528]/15 border border-[#F1B528]/30 px-4 py-1.5 rounded-full inline-block">
                  Kom godt i gang
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                  Har du en specifik maleropgave?
                </h2>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                  Uanset om du skal have malet hele huset, renoveret kontoret eller har brug for sparring om spartling og filt, står vi klar med råd og et uforpligtende tilbud.
                </p>
              </div>

              {/* Direct Info Boxes */}
              <div className="space-y-4">
                <a
                  href="tel:+4575695900"
                  className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#F1B528] transition-all group"
                >
                  <div className="p-3.5 rounded-xl bg-[#F1B528] text-slate-950 font-bold shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-500">Ring direkte</span>
                    <span className="text-lg font-bold text-slate-900 group-hover:text-[#D99C1B] transition-colors">
                      75 69 59 00
                    </span>
                  </div>
                </a>

                <a
                  href="mailto:bm@bmmaler.dk"
                  className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#F1B528] transition-all group"
                >
                  <div className="p-3.5 rounded-xl bg-[#F1B528] text-slate-950 font-bold shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-500">Skriv en e-mail</span>
                    <span className="text-base font-bold text-slate-900 group-hover:text-[#D99C1B] transition-colors">
                      bm@bmmaler.dk
                    </span>
                  </div>
                </a>
              </div>

              {/* Trust Box */}
              <div className="p-6 rounded-2xl bg-slate-950 text-white space-y-3">
                <div className="flex items-center gap-2 text-[#F1B528] font-bold text-sm">
                  <ShieldCheck className="w-5 h-5" />
                  <span>Danske Malermestres Garantiordning</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  BM Maler er medlem af Danske Malermestre. Det er din garanti for, at arbejdet udføres fagmæssigt korrekt og efter gældende standarder.
                </p>
              </div>
            </div>

            {/* Right Column: Interactive Contact Form */}
            <div className="lg:col-span-7 bg-slate-50 p-6 sm:p-10 rounded-3xl border border-slate-200/80 shadow-sm">
              <div className="mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Få et uforpligtende tilbud på din opgave
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">
                  Udfyld formularen, så vender vi hurtigt tilbage til dig.
                </p>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
